import { Loader } from '@googlemaps/js-api-loader';

// import { setupFetchDialog, setupFetchFauxDialog } from './dialogs';

/**
 * Creates a nice new Google map
 *
 * @export
 * @param {HTMLElement} mapContainer * Container that holds the map
 * @param {String} mapKey * Google API key
 * @param {Object} options * An object
 *
 */
class MapComponent {
  constructor(mapContainer, mapKey, options) {
    this.container = mapContainer;
    this.key = mapKey;
    this.center = options.center;
    this.zoom = options.zoom;
    this.maxZoom = options.maxZoom ? options.maxZoom : 18;
    this.fullscreenControl =
      options.fullscreenControl !== null ? options.fullscreenControl : true;
    this.stylesPath = options.styles ? options.styles : false;
    this.markers = options.markers;
    this.markerObjects = [];
    this.clickableMarkers = options.clickableMarkers || false;
    this.drawableLine = options.drawableLine || false;
    this.centerMarkers = options.centerMarkers ? options.centerMarkers : false;

    this.zoomControl =
      options.zoomControl !== null ? options.zoomControl : true;
    this.init();
  }

  async init() {
    async function loadStyle(styleUri) {
      const response = await fetch(styleUri, {
        credentials: 'same-origin',
      });

      const parsedResponse = await response.json();

      return parsedResponse;
    }

    const loaderConfig = {
      apiKey: this.key,
      version: 'weekly',
    };

    if (typeof this.stylesPath === 'string') {
      try {
        this.styles = await loadStyle(this.stylesPath);
      } catch (e) {
        // error
        // console.log(e);
      }
    }

    const loader = new Loader(loaderConfig);

    loader.load().then((google) => {
      let hasDragged = false;

      const bounds = new google.maps.LatLngBounds();

      if (this.markers) {
        this.markers.forEach((marker) => {
          bounds.extend({
            lat: marker.lat,
            lng: marker.lng,
          });
        });
      }

      this.map = new google.maps.Map(this.container, {
        center: this.center,
        zoom: this.zoom,
        draggable: true,
        scrollwheel: true,
        mapTypeControl: false,
        styles: this.styles,
        maxZoom: this.maxZoom,
        minZoom: 2,
        streetViewControl: false,
        fullscreenControl: this.fullscreenControl,
        zoomControl: this.zoomControl,
        zoomControlOptions: {
          position: google.maps.ControlPosition.LEFT_BOTTOM,
        },
      });

      if (this.markers) {
        this.markers.forEach((marker) => {
          this.addMarkers(google, marker, this.clickableMarkers);
        });
      }

      if (this.centerMarkers) {
        this.centreMapAroundMarkers(google, this.map);
      }

      // map lines
      if (this.drawableLine) {
        this.drawLineOnMap(google);
      }

      this.map.addListener('drag', () => {
        if (hasDragged === false) {
          hasDragged = true;
        }
      });

      window.onresize = () => {
        // recenters the google map
        function recenter(googleObject, map, location) {
          setTimeout(() => {
            googleObject.maps.event.trigger(map, 'resize');
            map.panTo(location);
          }, 10);
        }

        google.maps.event.trigger(this.map, 'resize');

        if (this.centerMarkers) {
          this.centreMapAroundMarkers(google, this.map);
        } else if (this.center) {
          if (hasDragged) {
            const currentCentre = this.map.getCenter();
            recenter(google, this.map, {
              lat: currentCentre.lat(),
              lng: currentCentre.lng(),
            });
          } else {
            recenter(google, this.map, {
              lat: this.center.lat,
              lng: this.center.lng,
            });
          }
        }
      };
    });
  }

  drawLineOnMap(google) {
    const path = this.markers
      .filter((marker) => marker.onRoute)
      .map(({ lat, lng }) => ({ lat, lng }));

    const lineSymbol = {
      path: 'M 0,-1 0,0',
      strokeOpacity: 1,
      scale: 3,
    };

    const linePath = new google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#00a6bc',
      strokePattern: 'Dashed',
      strokeOpacity: 0,
      icons: [
        {
          icon: lineSymbol,
          offset: '0',
          repeat: '9px',
        },
      ],
    });

    linePath.setMap(this.map);
  }

  addMarkers(google, marker, clickable) {
    const mapMarker = new google.maps.Marker({
      position: {
        lat: marker.lat,
        lng: marker.lng,
      },
      map: this.map,
      clickable,
      title: marker.label,
      animation: google.maps.Animation.DROP,
      icon: {
        // size: new google.maps.Size(21, 35),
        url: marker.icon,
      },
    });

    this.markerObjects.push(mapMarker);

    // Add event listeners to markers that reveal content
    if (marker.target && marker.activeClass) {
      mapMarker.addListener('click', () => {
        const targetToActivate = document.querySelector(marker.target);

        // Add the active class to the target
        if (!targetToActivate.classList.contains(marker.activeClass)) {
          targetToActivate.classList.add(marker.activeClass);
        }

        // Remove the class from all others
        Array.from(document.querySelectorAll(`.${marker.activeClass}`)).forEach(
          (target) => {
            if (target !== targetToActivate) {
              target.classList.remove(marker.activeClass);
            }
          }
        );
      });
    }

    // Add custom callback
    mapMarker.addListener('click', () => marker.callback());
  }

  centreMapAroundMarkers(google, map) {
    const bounds = new google.maps.LatLngBounds();

    this.markerObjects.forEach((marker) => {
      bounds.extend(marker.getPosition());
    });

    setTimeout(() => {
      map.fitBounds(bounds);
    }, 500);
  }
}

export function setupMaps(element = null, config = null) {
  const processMapElement = (map, config) => {
    const mapObjectName = map.getAttribute('id');

    // eslint-disable-next-line
    new MapComponent(
      map,
      config ? config.key : window[mapObjectName].key,
      config ? config.options : window[mapObjectName].options
    );
  };

  // If coming from react and using a ref
  if (element) {
    processMapElement(element, config);
  } else {
    Array.from(document.querySelectorAll('.js-map')).forEach((map) =>
      processMapElement(map)
    );
  }
}

export default { setupMaps };
